import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form"
import Section from "../../section"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { File, Loader2, UploadCloud, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import Dropzone, { type DropzoneState } from "shadcn-dropzone"
import { toast } from "sonner"
import { Textarea } from "@/components/ui/textarea"
import { useEffect, useMemo, useState } from "react"
import { usePayment } from "../../use-payment"
import { useGetLoanProviders } from "@/hooks/use-loan"
import {
	avanseRelationSchema,
	fibeRelationSchema,
	propelldRelationSchema,
} from "../../schemas"
import { useOfflinePayment } from "../hooks/use-offline"
import { useQueryClient } from "@tanstack/react-query"
import { useFormStore } from "@/stores/use-form-store"
import { useShallow } from "zustand/react/shallow"
import { FeeCategory } from "@/lib/types"

const LoanOfflinePaymentForm = ({ finalAmout }: { finalAmout: number }) => {
	const [isSelectedOther, setIsSelectedOther] = useState<boolean>(false)
	const { loanOfflinePaymentForm, course_id, setSelectedPaymentPlan } =
		usePayment()
	const { form } = loanOfflinePaymentForm
	const { data: loanProviders, isLoading: isLoadingLoanProviders } =
		useGetLoanProviders({
			course_id,
		})

	const currentRelations = useMemo(() => {
		const currentLoanProvider = loanProviders?.find(
			provider => provider.name === form.watch("loan_vendor"),
		)
		if (!currentLoanProvider) return fibeRelationSchema.options
		if (currentLoanProvider.identifier === "PROPELLD") {
			return propelldRelationSchema.options
		}
		if (currentLoanProvider.identifier === "AVANSE") {
			return avanseRelationSchema.options
		}
		return fibeRelationSchema.options
	}, [loanProviders, form.watch("loan_vendor")])

	useEffect(() => {
		form.setValue("final_payable_amount", finalAmout)
	}, [finalAmout])

	const { useGetOfflineLoanPaymentDetails, useGetFile } = useOfflinePayment()
	const queryClient = useQueryClient()
	const { mutateAsync: fetchFile } = useGetFile()
	const {
		// isPreview,
		config: formConfig,
		applicationId,
	} = useFormStore(
		useShallow(state => ({
			// isPreview: state.isPreview,
			config: state.config,
			applicationSubmission: state.applicationSubmission,
			applicationId: state.applicationId,
			verifiedFields: state.verifiedFields,
			setVerifiedFields: state.setVerifiedFields,
		})),
	)

	const paymentStatusQuery: any = queryClient.getQueryData([
		"check-payment-status",
		applicationId,
		formConfig?.batch_id,
		course_id,
		window.__DF_MODE,
		FeeCategory.TUITION,
		1,
	])

	const { data: loanOfflinePaymentDetails } = useGetOfflineLoanPaymentDetails(
		{
			loan_id: paymentStatusQuery?.details?.loan_id || "",
		},
	)

	useEffect(() => {
		if (loanOfflinePaymentDetails) {
			form.setValue("loan_id", loanOfflinePaymentDetails.lender_loan_id)
			form.setValue(
				"applicant_name",
				loanOfflinePaymentDetails.applicant_name,
			)
			form.setValue(
				"applicant_mobile",
				Number(loanOfflinePaymentDetails.applicant_mobile_number),
			)
			form.setValue(
				"applicant_email",
				loanOfflinePaymentDetails.applicant_email,
			)
			form.setValue(
				"requested_loan_amount",
				Number(loanOfflinePaymentDetails.loan_amount),
			)
			form.setValue("remarks", loanOfflinePaymentDetails.remarks)

			setTimeout(() => {
				form.setValue("loan_vendor", loanOfflinePaymentDetails.lender)
				form.setValue(
					"applicant_relation",
					loanOfflinePaymentDetails.applicant_relation,
				)
			}, 100)
		}
	}, [loanOfflinePaymentDetails])

	useEffect(() => {
		if (loanOfflinePaymentDetails) {
			setSelectedPaymentPlan(
				`loan-${loanOfflinePaymentDetails?.additional_info?.plan_id || ""}`,
			)
			fetchFile({
				file_url: loanOfflinePaymentDetails?.proof_url,
			})
				.then((file: File) => {
					form.reset({
						applicant_email:
							loanOfflinePaymentDetails?.applicant_email,
						applicant_mobile: Number(
							loanOfflinePaymentDetails?.applicant_mobile_number,
						),
						applicant_name:
							loanOfflinePaymentDetails?.applicant_name,
						applicant_relation:
							loanOfflinePaymentDetails?.applicant_relation,
						loan_id: loanOfflinePaymentDetails?.lender_loan_id,
						loan_vendor: loanOfflinePaymentDetails?.lender,
						requested_loan_amount: Number(
							loanOfflinePaymentDetails?.loan_amount,
						),
						proof_of_loan_disbursal: file,
						remarks: loanOfflinePaymentDetails?.remarks,
					})
					if (
						loanProviders?.find(
							provider =>
								provider.name ===
								loanOfflinePaymentDetails?.lender,
						)
					) {
						setIsSelectedOther(false)
					} else {
						setIsSelectedOther(true)
					}
					form.setValue(
						"loan_vendor",
						loanOfflinePaymentDetails?.lender,
					)
				})
				.catch(() => {
					toast.error(
						"Error Fetching Payment Details. Please refresh and try again",
					)
				})
		}
	}, [loanOfflinePaymentDetails])

	return (
		<Section
			title="Offline Payment Details"
			description="Use this option If you’ve already completed your payment through an offline method"
			fields={[
				<FormField
					key={"loan_id"}
					control={form.control}
					name="loan_id"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								Loan ID <span className="text-red-500">*</span>
							</FormLabel>
							<Input
								{...field}
								placeholder="Enter Loan ID"
							/>
							<FormMessage />
						</FormItem>
					)}
				/>,
				<FormField
					key={"loan_vendor"}
					control={form.control}
					name="loan_vendor"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								Loan Vendor{" "}
								<span className="text-red-500">*</span>
							</FormLabel>
							<FormControl>
								{isSelectedOther ? (
									<div className="relative">
										<Input
											{...field}
											placeholder="Enter Loan Vendor"
										/>
										<Button
											variant={"ghost"}
											className="absolute bottom-1/2 right-1 translate-y-1/2 cursor-pointer py-1"
											onClick={(e: React.MouseEvent) => {
												e.preventDefault()
												setIsSelectedOther(false)
												field.onChange(undefined)
											}}
										>
											<X />
										</Button>
									</div>
								) : (
									<Select
										value={field.value}
										disabled={field.disabled}
										onValueChange={value => {
											if (value == "OTHER") {
												setIsSelectedOther(true)
												field.onChange(undefined)
												form.setFocus("loan_vendor")
											} else {
												setIsSelectedOther(false)
												field.onChange(value)
											}
											form.trigger("loan_vendor")
										}}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select Loan Vendor" />
										</SelectTrigger>
										<SelectContent>
											{isLoadingLoanProviders ? (
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											) : (
												loanProviders?.map(provider => (
													<SelectItem
														key={provider.lender_id}
														value={provider.name}
													>
														{provider.display_name}
													</SelectItem>
												))
											)}
											<SelectItem
												key={"other"}
												value={"OTHER"}
											>
												OTHER
											</SelectItem>
										</SelectContent>
									</Select>
								)}
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>,
				<FormField
					key={"applicant_relation"}
					control={form.control}
					name="applicant_relation"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								Applicant Relation{" "}
								<span className="text-red-500">*</span>
							</FormLabel>
							<FormControl>
								<Select
									value={field.value}
									disabled={field.disabled}
									onValueChange={value => {
										field.onChange(value)
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select Relation" />
									</SelectTrigger>
									<SelectContent>
										{currentRelations.map(relation => (
											<SelectItem
												key={relation}
												value={relation}
											>
												{relation}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>,
				<FormField
					key={"applicant_name"}
					control={form.control}
					name="applicant_name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								Applicant Name{" "}
								<span className="text-red-500">*</span>
							</FormLabel>
							<FormControl>
								<Input
									type="text"
									{...field}
									placeholder="Enter Applicant Name"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>,
				<FormField
					key={"applicant_mobile"}
					control={form.control}
					name="applicant_mobile"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								Applicant Mobile{" "}
								<span className="text-red-500">*</span>
							</FormLabel>
							<FormControl>
								<Input
									type="number"
									{...field}
									placeholder="Enter Applicant Mobile"
									onWheel={e => e.currentTarget.blur()}
									onKeyDown={e => {
										if (
											["e", "E", "+", "-"].includes(e.key)
										) {
											e.preventDefault()
										}
									}}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>,
				<FormField
					key={"applicant_email"}
					control={form.control}
					name="applicant_email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								Applicant Email{" "}
								<span className="text-red-500">*</span>
							</FormLabel>
							<FormControl>
								<Input
									type="email"
									{...field}
									placeholder="Enter Applicant Email"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>,
				<FormField
					key={"requested_loan_amount"}
					control={form.control}
					name="requested_loan_amount"
					render={({ field }) => (
						<FormItem className="col-span-2 w-full">
							<FormLabel>
								Requested Loan Amount{" "}
								<span className="text-red-500">*</span>
							</FormLabel>
							<FormControl>
								<Input
									type="number"
									{...field}
									placeholder="Enter Paid Amount"
									onWheel={e => e.currentTarget.blur()}
									onKeyDown={e => {
										if (
											["e", "E", "+", "-"].includes(e.key)
										) {
											e.preventDefault()
										}
									}}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>,
				<FormField
					key={"proof_of_loan_disbursal"}
					// control={offlinePaymentForm.form.control}
					name="proof_of_loan_disbursal"
					render={({ field: { onChange, value } }) => (
						<FormItem className="col-span-2 w-full">
							<FormLabel className="flex flex-col space-y-1">
								<div>
									Proof of Loan Disbursal{" "}
									<span className="text-red-500">*</span>
									<span className="text-muted-foreground">
										(Max 5mb)
									</span>
								</div>
								<div className="text-xs italic">
									<span className="text-muted-foreground">
										In case of multiple Loan Disbursal
										proofs combine it to a single file
									</span>
								</div>
							</FormLabel>
							<FormControl>
								{value ? (
									<div className="flex w-full flex-col items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm md:w-1/2 md:flex-row">
										<div className="flex items-center space-x-3">
											{value.type.startsWith("image/") ? (
												<div className="relative h-12 w-full overflow-hidden rounded-md border border-gray-300 md:w-12">
													<img
														src={URL.createObjectURL(
															value,
														)}
														alt="Proof of Payment Preview"
														className="h-full w-full object-cover"
													/>
												</div>
											) : (
												<div className="flex h-12 w-full items-center justify-center rounded-md bg-blue-100 text-blue-600 md:w-12">
													<File className="h-6 w-6" />
												</div>
											)}
											<div className="flex flex-col">
												<span className="w-full truncate text-sm font-medium md:max-w-[200px]">
													{value.name}
												</span>
												<span className="text-xs text-muted-foreground">
													{(
														value.size /
														1024 /
														1024
													).toFixed(2)}{" "}
													MB
												</span>
											</div>
										</div>

										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() => onChange(undefined)}
											className="mt-2 text-red-500 hover:bg-red-50 md:mt-0"
										>
											<X className="mr-1 h-4 w-4" />
										</Button>
									</div>
								) : (
									<Dropzone
										onDrop={(acceptedFiles: File[]) => {
											onChange(acceptedFiles[0])
										}}
										dropZoneClassName="border-none bg-[#1D6AD30D]	 h-[20vh] "
										containerClassName="border border-dashed border-blue-500 rounded-md md:w-1/2 w-full"
										accept={{
											"image/jpeg": [".jpeg", ".jpg"],
											"image/png": [".png"],
											"application/pdf": [".pdf"],
										}}
										maxFiles={1}
										maxSize={5 * 1024 * 1024}
										onDropRejected={fileRejections => {
											fileRejections.forEach(
												rejection => {
													const { file, errors } =
														rejection
													errors.forEach(error => {
														if (
															error.code ===
															"file-too-large"
														) {
															toast.error(
																`${file.name} is too big. Max size is 5MB.`,
															)
														} else if (
															error.code ===
															"file-invalid-type"
														) {
															toast.error(
																`File type not supported. Please upload JPEG, PNG, or PDF`,
															)
														}
													})
												},
											)
										}}
									>
										{(dropzone: DropzoneState) => (
											<>
												{dropzone.isDragAccept ? (
													<div className="text-sm font-medium">
														Drop your files here!
													</div>
												) : (
													<div className="flex flex-col items-center justify-center space-y-2">
														<div className="flex items-center justify-center space-x-3">
															<div>
																<UploadCloud className="h-12 w-12 text-blue-500" />
															</div>
															<div className="flex flex-col items-center justify-center space-y-1">
																<div className="flex items-center gap-0.5 font-medium">
																	Drop & Drop
																	files or{" "}
																	<span className="text-blue-500 underline">
																		Browse
																	</span>
																</div>
																<div className="text-xs text-muted-foreground">
																	Supported
																	formats:
																	JPEG, PNG,
																	PDF
																</div>
															</div>
														</div>
													</div>
												)}
											</>
										)}
									</Dropzone>
								)}
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>,
				<FormField
					key={"remarks"}
					control={form.control}
					name="remarks"
					render={({ field }) => (
						<FormItem className="col-span-2">
							<FormLabel>Remarks</FormLabel>
							<FormControl>
								<Textarea
									{...field}
									placeholder="Add Remarks"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>,
			]}
		/>
	)
}

export default LoanOfflinePaymentForm
